import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiGlobe, FiMonitor, FiSmartphone, FiTablet } from 'react-icons/fi';
import { useStore } from '../store';
import toast from 'react-hot-toast';

const AnalyticsPage: React.FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const { fetchAnalytics } = useStore();
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (hash) {
      loadAnalytics();
    }
  }, [hash]);

  const loadAnalytics = async () => {
    try {
      const data = await fetchAnalytics(hash!);
      setAnalytics(data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'desktop':
        return <FiMonitor className="w-4 h-4" />;
      case 'mobile':
        return <FiSmartphone className="w-4 h-4" />;
      case 'tablet':
        return <FiTablet className="w-4 h-4" />;
      default:
        return <FiMonitor className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Analytics Not Available</h2>
        <p className="text-gray-600 mb-8">Unable to load analytics for this link.</p>
        <Link to="/dashboard" className="btn btn-primary">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/dashboard" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4">
            <FiArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Link Analytics</h1>
          <p className="text-gray-600 mt-2">Detailed privacy-preserving analytics for your link</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Total Clicks</p>
          <p className="text-3xl font-bold text-gray-900">{analytics.summary.totalClicks}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Last 30 Days</p>
          <p className="text-3xl font-bold text-gray-900">{analytics.summary.last30Days}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Unique Visitors</p>
          <p className="text-3xl font-bold text-gray-900">{analytics.summary.uniqueVisitors}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Trackers Blocked</p>
          <p className="text-3xl font-bold text-green-600">{analytics.summary.trackersBlocked}</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Clicks */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Clicks (Last 30 Days)</h3>
          <div className="space-y-2">
            {analytics.daily.slice(0, 7).map((day: any) => (
              <div key={day.date} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{new Date(day.date).toLocaleDateString()}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{ width: `${(day.clicks / Math.max(...analytics.daily.map((d: any) => d.clicks))) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-10 text-right">{day.clicks}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Countries */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Countries</h3>
          <div className="space-y-2">
            {analytics.countries.map((country: any) => (
              <div key={country.code} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FiGlobe className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{country.code || 'Unknown'}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{country.clicks} clicks</span>
              </div>
            ))}
          </div>
        </div>

        {/* Devices */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Types</h3>
          <div className="space-y-2">
            {analytics.devices.map((device: any) => (
              <div key={device.type} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {getDeviceIcon(device.type)}
                  <span className="text-sm text-gray-600">{device.type}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{device.clicks} clicks</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hourly Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Activity</h3>
          <div className="grid grid-cols-12 gap-1">
            {Array.from({ length: 24 }, (_, i) => {
              const hourData = analytics.hourly.find((h: any) => h.hour === i);
              const clicks = hourData?.clicks || 0;
              const maxClicks = Math.max(...analytics.hourly.map((h: any) => h.clicks), 1);
              const height = (clicks / maxClicks) * 100;
              
              return (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-full bg-gray-200 rounded-t" style={{ height: '60px' }}>
                    <div
                      className="w-full bg-primary-600 rounded-t transition-all duration-300"
                      style={{ height: `${height}%`, marginTop: `${60 - (height * 0.6)}px` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500 mt-1">{i % 2 === 0 ? i : ''}</span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">Hours (24h format)</p>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-800">
          <strong>Privacy Notice:</strong> All analytics data is aggregated and anonymized. 
          We never track individual users, store IP addresses, or use cookies. 
          Location data is limited to country level only.
        </p>
      </div>
    </div>
  );
};

export default AnalyticsPage;